import { DAY_WIDTH } from '../constants';

// Timeline zoom levels. MONTHLY (default): one column per day, top header row = months.
// DAILY: each day is split into `columnsPerDay` columns of 6 hours, top row = days.
export type ZoomLevel = 'monthly' | 'daily';

export type ZoomConfig = {
  // How many columns one calendar day is split into (monthly = 1, daily = 4 × 6h).
  columnsPerDay: number;
  // Pixel width of ONE column at this zoom.
  columnWidth: number;
};

export const getZoomConfig = (zoom: ZoomLevel): ZoomConfig =>
  zoom === 'daily'
    ? { columnsPerDay: 4, columnWidth: DAY_WIDTH } // same cell width as Monthly; day = 4×
    : { columnsPerDay: 1, columnWidth: DAY_WIDTH };

export const DAY_MS = 24 * 60 * 60 * 1000;
const FIFTEEN_MIN_MS = 15 * 60 * 1000;

export const startOfDayMs = (ms: number): number => {
  const x = new Date(ms);
  x.setHours(0, 0, 0, 0);
  return x.getTime();
};

// Fractional COLUMN position of `ms` from the range start, generalised across zooms.
// = (calendar day index from range start) × columnsPerDay
//   + (intra-day fraction) × columnsPerDay
// DST-safe day index (from the date's own calendar day, like the original day math).
// `snap` rounds the intra-day time to the 15-min grid ('floor' for a span's start,
// 'ceil' for its end) so a tile fully contains its booking; omit for exact (bands,
// today line). Multiply the result by columnWidth to get px.
//   monthly (cpd=1): dayIndex + dayFraction  (identical to the previous dayPosition)
//   daily   (cpd=4): dayIndex*4 + dayFraction*4  → 13:00 → dayIndex*4 + 2.16
export const columnPosition = (
  ms: number,
  rangeStartMs: number,
  columnsPerDay: number,
  snap?: 'floor' | 'ceil'
): number => {
  const dayStartMs = startOfDayMs(ms);
  const dayIndex = Math.round((dayStartMs - rangeStartMs) / DAY_MS);
  let intra = ms - dayStartMs;
  if (snap === 'floor') {
    intra = Math.floor(intra / FIFTEEN_MIN_MS) * FIFTEEN_MIN_MS;
  } else if (snap === 'ceil') {
    intra = Math.ceil(intra / FIFTEEN_MIN_MS) * FIFTEEN_MIN_MS;
  }
  return dayIndex * columnsPerDay + (intra / DAY_MS) * columnsPerDay;
};
